import { useEffect } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation, matchPath } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import KeoLamMoi from "components/KeoLamMoi";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeDark from "assets/theme-dark";

// Routes + auth
import { sidebarRoutes, hiddenRoutes } from "routes";
import ProtectedRoute from "components/ProtectedRoute";
import RoleGate from "components/RoleGate";
import DangNhap from "layouts/authentication/DangNhap";
import DatLaiMatKhau from "layouts/authentication/DatLaiMatKhau";
import { useAuth } from "context/AuthContext";
import TourRunner from "components/Tour";
import useLuongTour from "hooks/useLuongTour";
import useAmThanhThanhToan from "hooks/useAmThanhThanhToan";
import { BAT_HUONG_DAN } from "lib/batHuongDan";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setPageTitle } from "context";

// Images
import brandLogo from "assets/images/logo-thu-mua-bo.png";

const tatCaRoutes = [...sidebarRoutes, ...hiddenRoutes].filter((r) => r.route);

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const { layout, sidenavColor, darkMode } = controller;
  const { pathname } = useLocation();
  const { vaiTro } = useAuth();

  // Tour luồng lần đầu theo vai trò (nhân viên / kho) — tạm tắt cùng BAT_HUONG_DAN.
  useLuongTour();
  // Âm thanh tinh tinh khi NV gửi KT / KT nhận / đã thanh toán.
  useAmThanhThanhToan();

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    const khop = tatCaRoutes.find((r) => matchPath({ path: r.route, end: true }, pathname));
    const ten = khop?.name ?? null;
    setPageTitle(dispatch, ten);
    document.title = ten ? `${ten} · Thu mua bơ` : "Thu mua bơ";
  }, [dispatch, pathname]);

  const theoVaiTro = (allRoutes) => allRoutes.filter((r) => !r.roles || r.roles.includes(vaiTro));

  // Đăng ký route theo TOÀN BỘ danh sách gốc (không lọc theo vaiTro): vaiTro
  // tải bất đồng bộ từ Firestore nên ở lần render đầu (F5 / vào thẳng link)
  // nó còn là null. Nếu lọc route trước khi vaiTro sẵn sàng, react-router sẽ
  // không tìm thấy path khớp và rơi vào "*" -> redirect nhầm về "/". Việc
  // kiểm quyền thật sự nằm trong RoleGate, chạy sau khi đã tải xong vaiTro.
  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.route) {
        return (
          <Route
            path={route.route}
            element={<RoleGate roles={route.roles}>{route.component}</RoleGate>}
            key={route.key}
          />
        );
      }
      return null;
    });

  const sidebarChoVaiTro = theoVaiTro(sidebarRoutes);

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      <KeoLamMoi />
      {layout === "dashboard" && (
        <Sidenav
          color={sidenavColor}
          brand={brandLogo}
          brandName="Thu mua bơ"
          routes={sidebarChoVaiTro}
        />
      )}
      <Routes>
        <Route path="/dang-nhap" element={<DangNhap />} />
        <Route path="/dat-lai-mat-khau" element={<DatLaiMatKhau />} />
        <Route element={<ProtectedRoute />}>
          {getRoutes(sidebarRoutes)}
          {getRoutes(hiddenRoutes)}
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {BAT_HUONG_DAN ? <TourRunner /> : null}
    </ThemeProvider>
  );
}
